import React, { useCallback, useEffect } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import PropTypes from 'prop-types';

import { Button, Dialog, DialogActions, DialogTitle } from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import { RoleName } from '~/modules/common/components/entities/role';
import { DateRange } from '~/modules/common/components';
import { mapIsoStringtoUtcObject } from '~/modules/common/dates/convert';
import useTaskOwnedByResources from '../ReleaseResourceRequestDialog/hooks/useTaskOwnedByResources';
import { ChangeAllocationUserDialogContent } from './components';
import useStyles, { useDialogStyles } from './useStyles';
import {
  ALLOCATION_POSITION,
  useChangeAllocationUserAsOfDateDialogStateHandlers
} from './hooks';
import { ChangeAllocationUserOptionItemValue } from './components/constants';

const ChangeAllocationUserAsOfDateDialog = ({
  resourceAllocation,
  open,
  onClose,
  permissions,
  onConfirm,
  newResourceUser
}) => {
  const classes = useStyles();
  const intl = useIntl();
  const dialogClasses = useDialogStyles();
  const {
    prevOpen,
    selectedOption,
    changeDate,
    setSelectedOption,
    setPrevOpen,
    handleOptionSelect,
    handleDateChange,
    allocationStart,
    allocationEnd,
    allocationPosition,
    tasksChecked,
    setTasksChecked
  } = useChangeAllocationUserAsOfDateDialogStateHandlers({
    resourceAllocation
  });

  useEffect(() => {
    if (prevOpen !== open) {
      setPrevOpen(open);
      if (open) {
        setSelectedOption(null);
      }
    }
  }, [prevOpen, open, setPrevOpen, setSelectedOption]);

  const {
    resources,
    isLoading: isLoadingImpactedTasksSummary
  } = useTaskOwnedByResources({
    projectId: resourceAllocation.projectUri,
    resourceIds: resourceAllocation.user.userUri
  });

  const tasks = resources.length && resources[0].assignedTasks;

  const impactedTasks = (tasks || []).filter(task => {
    const asOfDate = ChangeAllocationUserOptionItemValue.FULL
      ? allocationStart
      : changeDate;

    const startDateInRange = task.startDate
      ? mapIsoStringtoUtcObject(task.startDate) <= allocationEnd
      : true;

    const endDateInRange = task.endDate
      ? asOfDate <= mapIsoStringtoUtcObject(task.endDate)
      : true;

    return startDateInRange && endDateInRange;
  });

  const getDisabled = useCallback(
    date => (!date ? false : date < allocationStart || date > allocationEnd),
    [allocationEnd, allocationStart]
  );

  const handleConfirm = useCallback(() => {
    if (onConfirm)
      onConfirm({
        asOfDate:
          allocationPosition === ALLOCATION_POSITION.CURRENT &&
          selectedOption === ChangeAllocationUserOptionItemValue.ASOFDATE
            ? changeDate
            : null,
        impactedTasks: tasksChecked ? impactedTasks : []
      });
  }, [
    onConfirm,
    impactedTasks,
    allocationPosition,
    selectedOption,
    changeDate,
    tasksChecked
  ]);

  const isAsOfDateDisabled =
    !selectedOption && allocationPosition === ALLOCATION_POSITION.CURRENT
      ? true
      : selectedOption === ChangeAllocationUserOptionItemValue.ASOFDATE
      ? getDisabled(changeDate)
      : false;

  return (
    <Dialog
      classes={dialogClasses}
      open={open}
      onClose={onClose}
      aria-labelledby="change-user-as-of-date-dialog-title"
      aria-describedby="change-user-as-of-date-dialog-description"
    >
      <DialogTitle id="change-user-as-of-date-dialog-title">
        {intl.formatMessage({
          id:
            allocationPosition === ALLOCATION_POSITION.CURRENT
              ? 'changeAllocationUserAsOfDateDialog.allocationInProgress'
              : allocationPosition === ALLOCATION_POSITION.PAST
              ? 'changeAllocationUserAsOfDateDialog.allocationInPast'
              : 'changeAllocationUserAsOfDateDialog.changeResourceText'
        })}
        <span className={classes.allocationSummary}>
          {resourceAllocation.role ? (
            <RoleName
              role={resourceAllocation.role}
              className={classes.roleName}
              showRate={false}
              showTooltip={false}
            />
          ) : (
            <span className={classes.noValue}>
              <FormattedMessage id="resourceRequestReadonlyItem.noRole" />
            </span>
          )}
          ,{' '}
          <DateRange
            start={allocationStart}
            end={allocationEnd}
            className={classes.allocationDates}
          />
        </span>
      </DialogTitle>
      {isLoadingImpactedTasksSummary ? (
        <Skeleton width="100%" height={70} />
      ) : (
        <ChangeAllocationUserDialogContent
          handleOptionSelect={handleOptionSelect}
          handleDateChange={handleDateChange}
          existingResourceUser={
            resourceAllocation.user && resourceAllocation.user.user
          }
          newResourceUser={newResourceUser}
          selectedOption={selectedOption}
          changeDate={changeDate}
          allocationPosition={allocationPosition}
          tasksChecked={tasksChecked}
          permissions={permissions}
          setTasksChecked={setTasksChecked}
          impactedTasks={impactedTasks}
          allocationStart={allocationStart}
          allocationEnd={allocationEnd}
        />
      )}
      <DialogActions>
        <Button onClick={onClose}>
          {intl.formatMessage({
            id: 'changeAllocationUserAsOfDateDialog.cancel'
          })}
        </Button>
        <Button
          onClick={handleConfirm}
          color="primary"
          autoFocus
          disabled={isAsOfDateDisabled}
        >
          {intl.formatMessage({
            id: 'changeAllocationUserAsOfDateDialog.changeResource'
          })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

ChangeAllocationUserAsOfDateDialog.propTypes = {
  resourceAllocation: PropTypes.object.isRequired,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  permissions: PropTypes.array,
  onConfirm: PropTypes.func.isRequired,
  newResourceUser: PropTypes.object.isRequired
};

export default ChangeAllocationUserAsOfDateDialog;
